import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';

// Orientation catalogue — single source of truth
export const VIDEO_FORMATS = [
  {
    id: 'landscape',
    label: 'Landscape',
    ratio: '16:9',
    helper: 'YouTube / Facebook',
    // silhouette dimensions (px) — tuned to match heights across options
    sw: 120,
    sh: 68,
  },
  {
    id: 'portrait',
    label: 'Portrait',
    ratio: '9:16',
    helper: 'TikTok / Reels',
    sw: 60,
    sh: 108,
  },
  {
    id: 'square',
    label: 'Square',
    ratio: '1:1',
    helper: 'Instagram',
    sw: 84,
    sh: 84,
  },
];

export const getFormatById = (id) =>
  VIDEO_FORMATS.find((f) => f.id === id) || VIDEO_FORMATS[0];

/**
 * Right-side drawer for selecting video format.
 * Tapping an option fires onSelect(id) which should also close the drawer.
 */
export default function VideoFormatDrawer({ open, onOpenChange, current, onSelect }) {
  const handlePick = (id) => {
    onSelect(id);
    // Give the purple highlight a frame to register, then close
    setTimeout(() => onOpenChange(false), 120);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md"
        data-testid="video-format-drawer"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-white text-xl">Choose Video Format</SheetTitle>
          <SheetDescription className="text-zinc-400 text-sm">
            Pick the shape that fits where you're sharing.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 grid grid-cols-3 gap-4">
          {VIDEO_FORMATS.map((fmt) => {
            const selected = fmt.id === current;
            return (
              <button
                key={fmt.id}
                type="button"
                onClick={() => handlePick(fmt.id)}
                data-testid={`format-option-${fmt.id}`}
                className={`group flex flex-col items-center justify-end gap-3 p-4 h-44 rounded-2xl border transition-all ${
                  selected
                    ? 'border-[#915ee9] bg-[#915ee9]/10 shadow-[0_0_0_1px_rgba(145,94,233,0.25),0_8px_24px_-8px_rgba(145,94,233,0.4)]'
                    : 'border-white/10 bg-white/[0.02] hover:border-[#915ee9]/40 hover:bg-[#915ee9]/[0.04]'
                }`}
              >
                {/* Silhouette */}
                <div className="flex-1 flex items-center justify-center w-full">
                  <div
                    className={`rounded-md transition-colors ${
                      selected
                        ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9]'
                        : 'bg-zinc-700 group-hover:bg-zinc-600'
                    }`}
                    style={{ width: fmt.sw, height: fmt.sh }}
                  />
                </div>

                {/* Label */}
                <div className="text-center">
                  <div
                    className={`text-sm font-medium ${
                      selected ? 'text-white' : 'text-zinc-200'
                    }`}
                  >
                    {fmt.label}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{fmt.ratio}</div>
                </div>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
